(() => {
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  function decorateMotion(root = document) {
    if (!document.body.classList.contains('am-motion-enabled')) return;
    const sections = root.matches?.('.am-page') ? [root] : [...root.querySelectorAll?.('.am-page') || []];
    sections.forEach((section) => {
      const staggerItems = section.querySelectorAll('.am-offer__benefit, .am-texture__step, .am-contrast__row, .am-faq details, .am-review, .am-trust__item, .am-formula__item, .am-social__card, .am-blog__card');
      const mobileMotion = window.matchMedia('(max-width: 749px)').matches;
      staggerItems.forEach((el, index) => {
        if (!el.hasAttribute('data-am-reveal')) el.setAttribute('data-am-reveal', '');
        /* Mobile gets a slightly longer stagger so each element has time to be noticed,
           while desktop keeps the established timing. */
        el.style.setProperty('--am-delay', `${Math.min(index, 7) * (mobileMotion ? 90 : 65)}ms`);
      });
      section.querySelectorAll('.am-hero__media, .am-texture__media, .am-offer__media, .am-flex-banner__media, .am-split-story__media').forEach((el) => {
        el.classList.add('am-media-reveal');
        if (!el.hasAttribute('data-am-reveal')) el.setAttribute('data-am-reveal', '');
      });
    });
  }

  function initReveal(root = document) {
    const items = [...root.querySelectorAll('[data-am-reveal]')].filter((el) => !el.dataset.amRevealBound);
    if (!items.length) return;
    /* Show the content rather than leaving invisible areas if motion is disabled or
       IntersectionObserver is unavailable (including older mobile webviews). */
    if (reducedMotion || !document.body.classList.contains('am-motion-enabled') ||
        document.body.classList.contains('am-motion-none') || !('IntersectionObserver' in window)) {
      items.forEach((el) => { el.classList.add('am-inview'); el.dataset.amRevealBound = 'true'; });
      return;
    }
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('am-inview');
          obs.unobserve(entry.target);
        }
      });
    }, {
      /* Start the same elegant reveal a little BEFORE content reaches the phone's
         viewport. The previous 18% threshold + -70px margin kept entire section
         introductions blank until visitors scrolled further (especially tall cards). */
      threshold: isMobile ? 0.01 : 0.06,
      rootMargin: isMobile ? '0px 0px 120px 0px' : '0px 0px 80px 0px'
    });
    /* Progressive enhancement: CSS only hides reveal items if this script is ready. */
    document.body.classList.add('am-reveal-ready');
    items.forEach((el) => { el.dataset.amRevealBound = 'true'; observer.observe(el); });
  }

  function storageAvailable() {
    try { localStorage.setItem('__am_test', '1'); localStorage.removeItem('__am_test'); return true; } catch (e) { return false; }
  }

  function initPopup(root = document) {
    root.querySelectorAll('.am-popup[data-enabled="true"]').forEach((popup) => {
      if (popup.dataset.bound === 'true') return;
      popup.dataset.bound = 'true';
      const designMode = popup.dataset.designMode === 'true';
      const key = `amiraya_popup_${popup.dataset.sectionId || 'default'}`;
      const trigger = popup.dataset.trigger || 'delay';
      const delay = Math.max(0, Number(popup.dataset.delay || 8)) * 1000;
      const scrollPercent = Math.max(5, Number(popup.dataset.scroll || 30));
      const repeatDays = Math.max(1, Number(popup.dataset.repeatDays || 14));
      const storageOK = storageAvailable();
      let shouldShow = true;
      let opened = false;
      if (!designMode && storageOK) {
        const stored = Number(localStorage.getItem(key) || 0);
        if (stored && Date.now() - stored < repeatDays * 86400000) shouldShow = false;
      }
      const markSeen = () => { if (!designMode && storageOK) localStorage.setItem(key, String(Date.now())); };
      const close = () => {
        popup.classList.remove('is-open'); popup.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('am-popup-lock'); markSeen();
      };
      const open = () => {
        if (opened || !shouldShow) return;
        opened = true;
        popup.classList.add('is-open'); popup.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('am-popup-lock');
      };
      popup.querySelectorAll('[data-am-popup-close]').forEach((el) => el.addEventListener('click', close));
      popup.querySelectorAll('[data-am-popup-action]').forEach((el) => el.addEventListener('click', markSeen));
      popup.querySelectorAll('[data-am-popup-copy]').forEach((btn) => btn.addEventListener('click', async () => {
        const code = btn.dataset.code;
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          btn.textContent = popup.dataset.amCopiedLabel || 'COPIED ✓';
        } catch (_) {
          btn.textContent = popup.dataset.amSelectCodeLabel || 'SELECT CODE';
          const range = document.createRange();
          range.selectNodeContents(btn.previousElementSibling);
          const selection = window.getSelection();
          selection.removeAllRanges(); selection.addRange(range);
        }
      }));
      popup.addEventListener('click', (e) => { if (e.target === popup) close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && popup.classList.contains('is-open')) close(); });
      if (!shouldShow) return;
      if (designMode) { popup._amOpen = open; popup._amClose = close; return; }
      if (trigger === 'hesitation') {
        let timeReady = false;
        let scrollReady = false;
        const maybeOpen = () => { if (timeReady && scrollReady) open(); };
        window.setTimeout(() => { timeReady = true; maybeOpen(); }, delay);
        const onHesitationScroll = () => {
          const doc = document.documentElement;
          const max = Math.max(1, doc.scrollHeight - window.innerHeight);
          if ((window.scrollY / max) * 100 >= scrollPercent) {
            scrollReady = true;
            window.removeEventListener('scroll', onHesitationScroll);
            maybeOpen();
          }
        };
        window.addEventListener('scroll', onHesitationScroll, { passive: true });
      } else if (trigger === 'scroll') {
        const onScroll = () => {
          const doc = document.documentElement;
          const max = Math.max(1, doc.scrollHeight - window.innerHeight);
          if ((window.scrollY / max) * 100 >= scrollPercent) { window.removeEventListener('scroll', onScroll); open(); }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      } else if (trigger === 'exit' && window.matchMedia('(hover:hover)').matches) {
        const onExit = (e) => { if (e.clientY <= 4) { document.removeEventListener('mouseout', onExit); open(); } };
        document.addEventListener('mouseout', onExit);
      } else {
        setTimeout(open, delay);
      }
    });
  }

  function initReviewCarousels(root = document) {
    root.querySelectorAll('[data-am-review-carousel]').forEach((rail) => {
      if (rail.dataset.amCarouselBound === 'true') return;
      rail.dataset.amCarouselBound = 'true';
      const section = rail.closest('.am-reviews');
      const prevButtons = section ? Array.from(section.querySelectorAll('[data-am-review-prev]')) : [];
      const nextButtons = section ? Array.from(section.querySelectorAll('[data-am-review-next]')) : [];
      const currentLabel = section?.querySelector('[data-am-review-current]');
      const desktopMq = window.matchMedia('(min-width: 750px)');
      const cards = () => Array.from(rail.querySelectorAll('.am-review'));
      let desktopIndex = 0;

      const step = () => {
        const card = rail.querySelector('.am-review');
        if (!card) return rail.clientWidth * .9;
        const styles = getComputedStyle(rail);
        const gap = parseFloat(styles.columnGap || styles.gap || 0);
        return card.getBoundingClientRect().width + gap;
      };
      const desktopVisible = () => {
        if (!section) return 3;
        const value = parseInt(getComputedStyle(section).getPropertyValue('--am-review-visible'), 10);
        return Number.isFinite(value) && value > 0 ? value : 3;
      };
      const desktopMaxIndex = () => Math.max(0, cards().length - desktopVisible());

      const updateCurrent = () => {
        if (!currentLabel) return;
        if (desktopMq.matches) {
          currentLabel.textContent = String(desktopIndex + 1);
          return;
        }
        const reviewCards = cards();
        if (!reviewCards.length) return;
        const amount = step();
        const nearest = amount ? Math.round(rail.scrollLeft / amount) : 0;
        currentLabel.textContent = String(Math.max(0, Math.min(reviewCards.length - 1, nearest)) + 1);
      };

      const renderDesktop = (animate = true) => {
        desktopIndex = Math.max(0, Math.min(desktopMaxIndex(), desktopIndex));
        rail.style.transitionDuration = animate && !reducedMotion ? '' : '0s';
        rail.style.transform = `translate3d(${-desktopIndex * step()}px, 0, 0)`;
        if (!animate || reducedMotion) {
          window.requestAnimationFrame(() => { rail.style.transitionDuration = ''; });
        }
        updateCurrent();
      };

      const move = (direction) => {
        if (desktopMq.matches) {
          desktopIndex = Math.max(0, Math.min(desktopMaxIndex(), desktopIndex + direction));
          renderDesktop(true);
          return;
        }
        rail.scrollBy({ left: direction * step(), behavior: reducedMotion ? 'auto' : 'smooth' });
        window.setTimeout(updateCurrent, reducedMotion ? 0 : 320);
      };

      prevButtons.forEach((button) => button.addEventListener('click', () => move(-1)));
      nextButtons.forEach((button) => button.addEventListener('click', () => move(1)));
      rail.addEventListener('scroll', () => {
        if (!desktopMq.matches) window.requestAnimationFrame(updateCurrent);
      }, { passive: true });

      const onModeOrSizeChange = () => {
        if (desktopMq.matches) {
          rail.scrollLeft = 0;
          renderDesktop(false);
        } else {
          rail.style.transform = '';
          rail.style.transitionDuration = '';
          desktopIndex = 0;
          rail.scrollLeft = 0;
          updateCurrent();
        }
      };
      window.addEventListener('resize', onModeOrSizeChange, { passive: true });
      if (desktopMq.addEventListener) desktopMq.addEventListener('change', onModeOrSizeChange);
      else if (desktopMq.addListener) desktopMq.addListener(onModeOrSizeChange);
      onModeOrSizeChange();

      const autoplay = rail.dataset.autoplay === 'true';
      const interval = Math.max(3000, Number(rail.dataset.interval || 6000));
      if (autoplay && !reducedMotion) {
        let timer;
        const start = () => {
          if (timer) window.clearInterval(timer);
          if (!desktopMq.matches) return;
          timer = window.setInterval(() => {
            const max = desktopMaxIndex();
            desktopIndex = desktopIndex >= max ? 0 : desktopIndex + 1;
            renderDesktop(true);
          }, interval);
        };
        const stop = () => { if (timer) { window.clearInterval(timer); timer = undefined; } };
        start();
        rail.addEventListener('mouseenter', stop);
        rail.addEventListener('mouseleave', start);
        rail.addEventListener('focusin', stop);
        rail.addEventListener('focusout', start);
        if (desktopMq.addEventListener) desktopMq.addEventListener('change', () => { stop(); start(); });
      }
    });
  }


  function initBlogCarousels(root = document) {
    root.querySelectorAll('[data-am-blog-carousel]').forEach((rail) => {
      if (rail.dataset.amBlogCarouselBound === 'true') return;
      rail.dataset.amBlogCarouselBound = 'true';
      const section = rail.closest('.am-blog');
      const prev = section?.querySelector('[data-am-blog-prev]');
      const next = section?.querySelector('[data-am-blog-next]');
      const current = section?.querySelector('[data-am-blog-current]');
      const cards = () => Array.from(rail.querySelectorAll('.am-blog__card'));
      const step = () => {
        const card = rail.querySelector('.am-blog__card');
        if (!card) return rail.clientWidth * .9;
        const styles = getComputedStyle(rail);
        return card.getBoundingClientRect().width + parseFloat(styles.columnGap || styles.gap || 0);
      };
      const indexNow = () => {
        const amount = step();
        if (!amount) return 0;
        return Math.max(0, Math.min(cards().length - 1, Math.round(rail.scrollLeft / amount)));
      };
      const update = () => { if (current) current.textContent = String(indexNow() + 1); };
      const go = (direction) => {
        const target = Math.max(0, Math.min(cards().length - 1, indexNow() + direction));
        rail.scrollTo({ left: target * step(), behavior: reducedMotion ? 'auto' : 'smooth' });
        window.setTimeout(update, reducedMotion ? 0 : 320);
      };
      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      rail.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });

      /* Mobile swipe without hijacking vertical page scrolling. We do not call
         preventDefault; a swipe is accepted only when horizontal intent is clear. */
      let startX = 0, startY = 0, swiped = false;
      rail.addEventListener('touchstart', (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches || !event.touches?.length) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        swiped = false;
      }, { passive: true });
      rail.addEventListener('touchend', (event) => {
        if (!window.matchMedia('(max-width: 749px)').matches || !event.changedTouches?.length) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) >= 46 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          swiped = true;
          go(dx < 0 ? 1 : -1);
        }
      }, { passive: true });
      rail.addEventListener('click', (event) => {
        if (!swiped) return;
        event.preventDefault();
        event.stopPropagation();
        swiped = false;
      }, true);
      update();
    });
  }


  function initAutoplayMediaFallbacks(root = document) {
    const wraps = root.matches?.('[data-am-autoplay-media-wrap]') ? [root] : [...root.querySelectorAll?.('[data-am-autoplay-media-wrap]') || []];
    wraps.forEach((wrap) => {
      if (wrap.dataset.amMediaFallbackBound === 'true') return;
      wrap.dataset.amMediaFallbackBound = 'true';
      const video = wrap.querySelector('video');
      if (!video) return;
      const markPlaying = () => wrap.classList.add('is-playing');
      const markFallback = () => { if (video.paused || video.ended) wrap.classList.remove('is-playing'); };
      video.addEventListener('playing', markPlaying);
      video.addEventListener('play', markPlaying);
      video.addEventListener('pause', markFallback);
      video.addEventListener('ended', markFallback);
      video.addEventListener('error', () => wrap.classList.remove('is-playing'));
      if (wrap.dataset.amForceAutoplay === 'true') {
        video.muted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        const tryAutoplay = () => {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        };
        tryAutoplay();
        video.addEventListener('loadedmetadata', tryAutoplay, { once: true });
        video.addEventListener('canplay', tryAutoplay, { once: true });
      }
      if (!video.paused && !video.ended && video.readyState >= 2) markPlaying();
    });
  }

  function initIngredientAnchorScroll(root = document) {
    if (reducedMotion || !window.matchMedia('(max-width: 749px)').matches) return;
    root.querySelectorAll('a[href="#ingredients"]').forEach((link) => {
      if (link.dataset.amIngredientScrollBound === 'true') return;
      link.dataset.amIngredientScrollBound = 'true';
      link.addEventListener('click', (event) => {
        const target = document.getElementById('ingredients');
        if (!target) return;
        event.preventDefault();
        const header = document.querySelector('.section-header, sticky-header, .header-wrapper');
        const headerHeight = header ? Math.min(120, header.getBoundingClientRect().height || 0) : 0;
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerHeight - 12);
        window.scrollTo({ top, behavior: 'smooth' });
        try { history.replaceState(null, '', '#ingredients'); } catch (_) {}
      });
    });
  }

  function init(root = document) { decorateMotion(root); initReveal(root); initPopup(root); initReviewCarousels(root); initBlogCarousels(root); initAutoplayMediaFallbacks(root); initIngredientAnchorScroll(root); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target.querySelector?.('.am-popup[data-enabled="true"]');
    popup?._amOpen?.();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const popup = event.target.querySelector?.('.am-popup[data-enabled="true"]');
    popup?._amClose?.();
  });
})();
